import {effect, EffectRef, inject, Injectable, signal, WritableSignal} from '@angular/core';
import {ApiService} from './api.service';
import {take} from 'rxjs';
import {LanguageService} from './language.service';
import {ProfileResource} from '../types/user/profileResource';
import {MaintenanceService} from './maintenance.service';
import {NavService} from './nav.service';
import {NotificationStateService} from './notification-state.service';
import {ResetPasswordEnforcementService} from './reset-password-enforcement.service';
import {TokenService} from './token.service';

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  private api: ApiService = inject(ApiService);
  private languageService: LanguageService = inject(LanguageService);
  private maintenanceService: MaintenanceService = inject(MaintenanceService);
  private navService: NavService = inject(NavService);
  private notificationStateService: NotificationStateService = inject(NotificationStateService);
  private resetPasswordEnforcementService: ResetPasswordEnforcementService = inject(ResetPasswordEnforcementService);
  private tokenService: TokenService = inject(TokenService);

  public profileAvatarDialog: WritableSignal<boolean> = signal(false);
  public profileAvatarUserID: WritableSignal<number> = signal(0);
  public profile: WritableSignal<ProfileResource | null> = signal(null);
  private isLoadingProfile: boolean = false;
  private loadQueued: boolean = false;
  private pendingLanguage: WritableSignal<string | null> = signal(null);

  public showProfile(userID: number): void {
    this.profileAvatarDialog.set(true);
    this.profileAvatarUserID.set(userID);
  }

  private languageUpdateEffect: EffectRef = effect(() => {
    const language = this.profile()?.user.language?.toLowerCase();
    if (!language) {
      return;
    }

    const pendingLanguage = this.pendingLanguage();
    if (pendingLanguage) {
      if (language === pendingLanguage) {
        this.pendingLanguage.set(null);
      } else {
        return;
      }
    }

    if (
      language !== this.languageService.getLanguage() ||
      !this.languageService.hasLoadedLanguage(language)
    ) {
      this.languageService.setLanguage(language);
    }
  })

  private tokenStateEffect: EffectRef = effect(() => {
    const tokenState = this.tokenService.tokenState();
    if (tokenState === 'set' || tokenState === 'loaded') {
      this.loadUserProfile();
    } else if (tokenState === 'cleared') {
      this.resetProfile();
    }
  });

  public loadUserProfile(queueIfLoading: boolean = false): void {
    if (this.isLoadingProfile) {
      if (queueIfLoading) {
        this.loadQueued = true;
      }
      return;
    }

    this.isLoadingProfile = true;
    this.api.getUserProfile()
      .pipe(take(1))
      .subscribe({
        next: (profile: ProfileResource) => {
          this.profile.set(profile);
          this.navService.reloadMenus();
          this.notificationStateService.resetState();
          this.notificationStateService.loadNotificationBadges();
          this.maintenanceService.runMaintenanceCheck();
          this.isLoadingProfile = false;
          if (this.loadQueued) {
            this.loadQueued = false;
            this.loadUserProfile();
          }
        },
        error: (error) => {
          console.error('Error loading profile:', error);
          this.isLoadingProfile = false;
          if (this.loadQueued) {
            this.loadQueued = false;
            this.loadUserProfile();
          }
        }
      });
  }

  public resetProfile(): void {
    this.profile.set(null);
    this.profileAvatarUserID.set(0);
    this.pendingLanguage.set(null);
    this.loadQueued = false;
    this.navService.resetUserMenus();
    this.notificationStateService.resetState();
    this.resetPasswordEnforcementService.clearPasswordChangeRequirement();
    this.languageService.setLanguageDefault();
  }

  public setProfileLanguage(language: string): void {
    const requestedLanguage = language.toLowerCase();
    this.pendingLanguage.set(requestedLanguage);

    this.api.setUserProfileLanguage(requestedLanguage)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.loadUserProfile(true);
        },
        error: (error) => {
          console.error('Error updating profile language:', error);
          this.pendingLanguage.set(null);
          const languageFromProfile = this.profile()?.user.language?.toLowerCase();
          if (languageFromProfile) {
            this.languageService.setLanguage(languageFromProfile);
          }
        }
      });
  }

  public setProfileAutoTranslation(autoTranslation: boolean): void {
    this.api.setUserProfileAutoTranslation(autoTranslation)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.loadUserProfile(true);
        },
        error: (error) => {
          console.error('Error updating profile auto translation:', error);
        }
      }); 
  }
}
